export type ChatRole = 'assistant' | 'user';
export type FeedbackSentiment = 'helpful' | 'not_helpful';
export type GuidedEntryMode = 'search_posts_guided' | 'rewrite_post_guided' | 'find_community_guided';

export interface ChatSuggestion {
  label: string;
  prompt: string;
  guidedMode?: GuidedEntryMode;
}

export type ChatActionKind = 'prompt' | 'use_draft' | 'navigate';

export interface ChatAction {
  kind: ChatActionKind;
  label: string;
  prompt?: string;
  route?: unknown[];
  queryParams?: Record<string, string>;
}

export interface ChatMessage {
  id: string;
  role: ChatRole;
  text: string;
  isStreaming: boolean;
  traceId?: string;
  intent?: string;
  agentName?: string;
  useCase?: string;
  crisis: boolean;
  blocked: boolean;
  followUp: boolean;
  followUpAction?: string;
  feedbackSent?: FeedbackSentiment;
  feedbackPending?: boolean;
  feedbackSubmitted?: boolean;
  feedbackError?: string;
  responseComplete: boolean;
  timestamp: Date;
  structured?: ChatStructuredContent | null;
  isGuidancePrompt?: boolean;
  isWelcomeMessage?: boolean;
}

export interface PostResultVm {
  postId: string;
  title: string;
  excerpt: string;
  communityId: string;
  communityName: string;
  replyCount?: number;
  helpfulReplyCount?: number;
}

export interface CommunityJoinVm {
  communityId: string;
  communityName: string;
  memberCount: number;
  description: string;
}

export interface PeerVm {
  handle: string;
  initials: string;
  sharedExperience: string;
}

export interface RewriteDraftVm {
  title: string;
  body: string;
  safetyNotes: string[];
}

export interface CommunityPalette {
  primary: string;
  bg: string;
  border: string;
  text: string;
}

export type ChatStructuredContent =
  | { kind: 'search_posts'; posts: PostResultVm[]; showMoreLabel?: string }
  | { kind: 'community'; community: CommunityJoinVm; starterPosts: string[] }
  | { kind: 'peer_finder'; peers: PeerVm[] }
  | { kind: 'rewrite_post'; draft: RewriteDraftVm }
  | { kind: 'crisis' };

export const COMMUNITY_COLOURS: Record<string, CommunityPalette> = {
  pcos: { primary: '#E74C8B', bg: '#FDEDF3', border: '#F4C0D1', text: '#72243E' },
  thyroid: { primary: '#2E86C1', bg: '#EBF5FB', border: '#85C1E9', text: '#0C447C' },
  diabetes: { primary: '#E67E22', bg: '#FDF2E9', border: '#F0B27A', text: '#633806' },
  'skin-acne': { primary: '#C65A8A', bg: '#FDEBF3', border: '#EFB7CF', text: '#6B2947' },
  'mental-health': { primary: '#8E44AD', bg: '#F4ECF7', border: '#C39BD3', text: '#3B1A5E' },
  general: { primary: '#0D9488', bg: '#f0fdfa', border: '#99F6E4', text: '#085041' },
};

export const DEFAULT_CHIPS: Record<string, string[]> = {
  search_posts: ['Search more', 'Find peers on this', 'Help me write about this'],
  find_community: ['Tell me more about this community', 'Show another community'],
  newly_diagnosed: ['Tell me more about this community', 'Show another community'],
  peer_finder: ['Search more peers', 'Find another match'],
  rewrite_post: ['Revise my draft', 'Start over'],
  crisis: ['Show helplines', 'I need immediate help'],
  unknown: ['Search posts', 'Rewrite', 'Find the right community', 'How does this work?']
};

export const FOLLOW_UP_CHIPS: Record<string, string[]> = {
  summarize_search_results: ['Summarise these results', 'Search again'],
  explain_search_results: ['Explain this', 'Find more like this'],
  search_more: ['Show more results', 'Search related topics'],
  search_related: ['Search related topics', 'Find peers on this'],
  search_refine_community: ['Search this community only', 'Search all communities'],
  revise_rewrite: ['Revise my draft', 'Start over'],
  simplify_response: ['Simplify this', 'Tell me more'],
  explain_community: ['Tell me more about this community', 'Join now'],
  alternate_community: ['Show another community', 'Join anyway'],
  compare_communities: ['Compare communities', 'Join this one'],
  suggest_tags: ['Suggest tags for my post', 'Post without tags'],
};

export function getCommunityPalette(communityId?: string | null): CommunityPalette {
  const key = normalizeCommunityKey(communityId);
  return COMMUNITY_COLOURS[key] ?? COMMUNITY_COLOURS['general'];
}

function normalizeCommunityKey(value?: string | null): string {
  const normalized = (value ?? 'general')
    .trim()
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

  if (!normalized) return 'general';
  if (normalized.includes('pcos') || normalized.includes('reproductive')) return 'pcos';
  if (normalized.includes('thyroid')) return 'thyroid';
  if (normalized.includes('diabetes') || normalized.includes('blood-sugar')) return 'diabetes';
  if (
    normalized.includes('skin')
    || normalized.includes('acne')
    || normalized.includes('skincare')
    || normalized.includes('dermatologist')
  ) {
    return 'skin-acne';
  }
  if (normalized.includes('mental') || normalized.includes('anxiety') || normalized.includes('depression')) {
    return 'mental-health';
  }

  return normalized;
}

export function communityLabelFromId(communityId?: string | null): string {
  const raw = (communityId ?? 'general').trim();
  if (!raw) return 'General';
  return raw
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
}
